import apiService from "@/core/services/apiService";

export type State = { classes: object };
import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import { Actions, Mutations } from "@/store/enums/store.enums";
import { ContentManagementActions, ContentManagementMutation } from "@/modules/cms/store/enums";
import store from "@/store";
import utilsService from "@/core/services/utilsService";

@Module
export default class Store extends VuexModule {
  renderKey = 1;

  listParams = {
    page: 1,
    results: 10,
    search: null,
    sort: [],
    filter: {}
  };
  listCache = [];
  listTotal = 0;
  listFilterOptions = {};

  listBlockParams = {
    page: 1,
    results: 10,
    search: null,
    sort: [],
    filter: {}
  };
  listBlockCache = [];
  listBlockTotal = 0;
  listBlockFilterOptions = {};
  headerTemplateOptions = [];
  footerTemplateOptions = [];
  navigationOptions = [];
  blockTypes = [];
  pendingBlocksCache = [];
  pendingBlocksTotal = 0;

  publishLogParams = {
    page: 1,
    results: 10,
    search: null,
    sort: [],
    filter: {}
  };
  publishLogCache = [];
  publishLogTotal = 0;

  promotionListParams = {
    page: 1,
    results: 10,
    search: null,
    sort: [],
    filter: {}
  };
  promotionListCache = [];
  promotionListTotal = 0;
  promotionOptions = [];

  get getContentManagementRenderKey() {
    return "cms-" + this.renderKey;
  }

  get getContentManagementLastListResult() {
    return this.listCache;
  }

  get getContentManagementBlockTypes() {
    return this.blockTypes;
  }

  get getContentManagementLastListTotal() {
    return this.listTotal;
  }

  get getContentManagementLastFilterOptions() {
    return this.listFilterOptions;
  }

  get getContentManagementBlocksLastListResult() {
    return this.listBlockCache;
  }

  get getContentManagementBlocksLastListTotal() {
    return this.listBlockTotal;
  }

  get getContentManagementBlocksLastFilterOptions() {
    return this.listBlockFilterOptions;
  }

  get getContentManagementHeaderTemplateOptions() {
    return this.headerTemplateOptions;
  }

  get getContentManagementFooterTemplateOptions() {
    return this.footerTemplateOptions;
  }

  get getContentManagementNavigationOptions() {
    return this.navigationOptions;
  }

  get getContentManagementPendingBlocks() {
    return this.pendingBlocksCache;
  }

  get getContentManagementPendingBlocksTotal() {
    return this.pendingBlocksTotal;
  }

  get getContentManagementPublishLog() {
    return this.publishLogCache;
  }

  get getContentManagementPublishLogTotal() {
    return this.publishLogTotal;
  }

  get getContentManagementPromotionsListResult() {
    return this.promotionListCache;
  }

  get getContentManagementPromotionsListTotal() {
    return this.promotionListTotal;
  }

  get getContentManagementPromotionOptions() {
    return this.promotionOptions;
  }

  @Mutation
  [ContentManagementMutation.SET_RENDER_UPDATE]() {
    this.renderKey++;
  }

  @Mutation
  [ContentManagementMutation.SET_LIST_RESULT]({ listParams, listTotal, listCache, listFilterOptions }) {
    this.listParams = listParams;
    this.listCache = listCache;
    this.listTotal = listTotal;
    this.listFilterOptions = listFilterOptions;
  }

  @Mutation
  [ContentManagementMutation.SET_BLOCKS_RESULT]({ listParams, listTotal, listCache, listFilterOptions }) {
    this.listBlockParams = listParams;
    this.listBlockCache = listCache;
    this.listBlockTotal = listTotal;
    this.listBlockFilterOptions = listFilterOptions;
  }

  @Mutation
  [ContentManagementMutation.SET_BLOCK_TYPES]({ blockTypes }) {
    this.blockTypes = blockTypes;
  }

  @Mutation
  [ContentManagementMutation.SET_NAVIGATION_OPTIONS]({
                                                       headerTemplateOptions,
                                                       footerTemplateOptions,
                                                       navigationOptions
                                                     }) {
    this.headerTemplateOptions = headerTemplateOptions;
    this.footerTemplateOptions = footerTemplateOptions;
    this.navigationOptions = navigationOptions;
  }

  @Mutation
  [ContentManagementMutation.SET_PENDING_BLOCKS_RESULT]({ pendingBlocksCache, pendingBlocksTotal }) {
    this.pendingBlocksCache = pendingBlocksCache;
    this.pendingBlocksTotal = pendingBlocksTotal;
  }

  @Mutation
  [ContentManagementMutation.SET_PUBLISH_LOG_RESULT]({ publishLogParams, publishLogTotal, publishLogCache }) {
    this.publishLogParams = publishLogParams;
    this.publishLogCache = publishLogCache;
    this.publishLogTotal = publishLogTotal;
  }

  @Mutation
  [ContentManagementMutation.SET_PROMOTIONS_RESULT]({ promotionListParams, promotionListTotal, promotionListCache }) {
    this.promotionListParams = promotionListParams;
    this.promotionListCache = promotionListCache;
    this.promotionListTotal = promotionListTotal;
  }

  @Mutation
  [ContentManagementMutation.SET_PROMOTION_OPTIONS]({ promotionOptions }) {
    this.promotionOptions = promotionOptions;
  }

  @Action
  [ContentManagementActions.REFRESH_LIST]() {
    this.context.dispatch(ContentManagementActions.FETCH_LIST, this.listParams);
  }

  @Action
  [ContentManagementActions.REFRESH_BLOCKS]() {
    this.context.dispatch(ContentManagementActions.FETCH_BLOCKS, this.listBlockParams);
  }

  @Action
  [ContentManagementActions.FETCH_LIST](params) {

    return new Promise<void>((resolve, reject) => {

      apiService.post("content/list", params).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // return results into storage for reactive update if needed //
        this.context.commit(ContentManagementMutation.SET_LIST_RESULT, {
          listParams: params,
          listTotal: data.results,
          listCache: data.result,
          listFilterOptions: data.filter
        });

        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.SEARCH_LIST](params) {

    return new Promise<void>((resolve, reject) => {

      apiService.post("content/list", params).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve(data);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.GET_REFERENCE_SETTINGS]({ referenceType }) {

    return new Promise<void>((resolve, reject) => {

      apiService.get(`content/get-reference-settings/${referenceType}`).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve(data);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.GET_REFERENCE_TOPICS]({ referenceType }) {

    return new Promise<void>((resolve, reject) => {

      apiService.get(`content/get-reference-topics/${referenceType}`).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve(data);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.GET_REFERENCE_OPTIONS](props) {

    return new Promise<void>((resolve, reject) => {

      apiService.post("content/get-reference-options/", props).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve(data);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.GET_SERVICE_OPTIONS](props) {

    return new Promise<void>((resolve, reject) => {

      apiService.post("content/get-service-options/", props).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve(data);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

    @Action
  [ContentManagementActions.GET_ADDRESSBOOK_OPTIONS](props) {

    return new Promise<void>((resolve, reject) => {

      apiService.post("content/get-addressbook-options/", props).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve(data);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.GET_PRODUCT_OPTIONS](props) {

    // Body wird als 2. arg an apiService.post übergeben (dort als AxiosRequestConfig
    // fehlt-typisiert, zur Laufzeit aber axios-post-Body) -> any, analog products-Store //
    const params: any = { search: props?.search || "", results: 50, page: 1 };

    return new Promise<void>((resolve, reject) => {

      apiService.post("products/list", params).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve(data);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.GET_CATEGORY_OPTIONS]() {

    return new Promise<void>((resolve, reject) => {

      apiService.get("products/categories").then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve(data);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.GET_ITEM](detailId) {

    return new Promise<void>((resolve, reject) => {

      apiService.get(`content/get/${detailId}`).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.GET_NAVIGATION_OPTIONS]() {

    return new Promise<void>((resolve, reject) => {

      apiService.get("content/get-navigation-options").then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // return results into storage for reactive update if needed //
        this.context.commit(ContentManagementMutation.SET_NAVIGATION_OPTIONS, {
          headerTemplateOptions: data.result.headerTemplateOptions,
          footerTemplateOptions: data.result.footerTemplateOptions,
          navigationOptions: data.result.navigationOptions
        });

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.GET_BLOCK_TYPES]() {

    return new Promise<void>((resolve, reject) => {

      apiService.get("content/get-block-types").then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // return results into storage for reactive update if needed //
        this.context.commit(ContentManagementMutation.SET_BLOCK_TYPES, {
          blockTypes: data.result
        });

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    }).catch((err) => {

      utilsService.showErrorToast(store.getters.getError);

    });

  };

  @Action
  [ContentManagementActions.CREATE_ITEM](payload) {

    return new Promise((resolve, reject) => {

      const { data } = payload;

      apiService.post("content/create/", data).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // flag cached keep alive comp to render new //
        this.context.commit(ContentManagementMutation.SET_RENDER_UPDATE);

        // refresh list in background//
        this.context.dispatch(ContentManagementActions.REFRESH_LIST);

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.UPDATE_ITEM](payload) {

    return new Promise((resolve, reject) => {

      const { editId, data } = payload;

      apiService.put(`content/update-base/${editId}`, data).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // refresh list in background//
        this.context.dispatch(ContentManagementActions.REFRESH_LIST);

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.UPDATE_META](payload) {

    return new Promise<void>((resolve, reject) => {

      const { editId, data } = payload;

      apiService.put(`content/update-meta/${editId}`, data).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.UPDATE_REFERENCE](payload) {

    return new Promise<void>((resolve, reject) => {

      const { editId, data } = payload;

      apiService.put(`content/update-reference/${editId}`, data).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.UPDATE_BLOCKS](payload) {

    return new Promise<void>((resolve, reject) => {

      const { editId, data } = payload;

      apiService.put(`content/update-blocks/${editId}`, data).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.UPDATE_ATTRIBUTES](payload) {

    return new Promise<void>((resolve, reject) => {

      const { editId, data } = payload;

      apiService.put(`content/update-attributes/${editId}`, data).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.DELETE_ITEM](editId) {

    return new Promise((resolve, reject) => {

      apiService.delete(`content/delete/${editId}`).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // refresh list in background//
        this.context.dispatch(ContentManagementActions.REFRESH_LIST);

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };


  @Action
  [ContentManagementActions.FETCH_BLOCKS](params) {

    return new Promise<void>((resolve, reject) => {

      apiService.post("content/list-blocks", params).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // return results into storage for reactive update if needed //
        this.context.commit(ContentManagementMutation.SET_BLOCKS_RESULT, {
          listParams: params,
          listTotal: data.results,
          listCache: data.result,
          listFilterOptions: data.filter
        });
        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.GET_BLOCK](detailId) {

    return new Promise<void>((resolve, reject) => {

      apiService.get(`content/get-block/${detailId}`).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.GET_BLOCK_PREVIEW](params) {

    const { blockId, lang, blockData } = params;

    return new Promise<void>((resolve, reject) => {

      apiService.post(`content/preview-block/${blockId}`, { ...blockData, lang }).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified || !data?.result?.preview) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve(data.result.preview);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.CREATE_BLOCK](payload) {

    return new Promise((resolve, reject) => {

      const { data } = payload;

      apiService.post("content/create-block/", data).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // flag cached keep alive comp to render new //
        this.context.commit(ContentManagementMutation.SET_RENDER_UPDATE);

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.UPDATE_BLOCK](payload) {

    return new Promise((resolve, reject) => {

      const { editId, data } = payload;

      apiService.put(`content/update-block/${editId}`, data).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.UPDATE_BLOCK_TRANSLATIONS](payload) {

    return new Promise<void>((resolve, reject) => {

      const { editId } = payload;

      apiService.put(`content/update-block-translations/${editId}`, payload).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.UPDATE_BLOCK_ATTRIBUTES](payload) {

    return new Promise<void>((resolve, reject) => {

      const { editId } = payload;

      apiService.put(`content/update-block-attributes/${editId}`, payload).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.DELETE_BLOCK](editId) {

    return new Promise((resolve, reject) => {

      apiService.delete(`content/delete-block/${editId}`).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // refresh list in background//
        this.context.dispatch(ContentManagementActions.REFRESH_BLOCKS);

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.FETCH_PENDING_BLOCKS]() {

    return new Promise<void>((resolve, reject) => {

      apiService.get("content/list-pending-blocks").then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        this.context.commit(ContentManagementMutation.SET_PENDING_BLOCKS_RESULT, {
          pendingBlocksCache: data.result,
          pendingBlocksTotal: data.results
        });

        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.PUBLISH_BLOCKS](blockIds) {

    return new Promise<void>((resolve, reject) => {

      apiService.post("content/publish-blocks", { blockIds } as any).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // refresh pending list //
        this.context.dispatch(ContentManagementActions.FETCH_PENDING_BLOCKS);

        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.FETCH_PUBLISH_LOG](params) {

    return new Promise<void>((resolve, reject) => {

      apiService.post("content/list-publish-log", params).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        this.context.commit(ContentManagementMutation.SET_PUBLISH_LOG_RESULT, {
          publishLogParams: params,
          publishLogTotal: data.results,
          publishLogCache: data.result
        });
        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.CLEAR_CACHE]() {

    return new Promise<void>((resolve, reject) => {

      apiService.delete("content/clear-cache").then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        this.context.dispatch(ContentManagementActions.FETCH_PENDING_BLOCKS);
        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };


  // ─── Promotions ──────────────────────────────────────────────────── //


  @Action
  [ContentManagementActions.REFRESH_PROMOTIONS]() {
    this.context.dispatch(ContentManagementActions.FETCH_PROMOTIONS, this.promotionListParams);
  }

  @Action
  [ContentManagementActions.FETCH_PROMOTIONS](params) {

    return new Promise<void>((resolve, reject) => {

      apiService.post("content/list-promotions", params).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        this.context.commit(ContentManagementMutation.SET_PROMOTIONS_RESULT, {
          promotionListParams: params,
          promotionListTotal: data.results,
          promotionListCache: data.result
        });

        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.GET_PROMOTION](detailId) {

    return new Promise((resolve, reject) => {

      apiService.get(`content/get-promotion/${detailId}`).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.CREATE_PROMOTION](payload) {

    return new Promise((resolve, reject) => {

      const { data } = payload;

      apiService.post("content/create-promotion/", data).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        this.context.commit(ContentManagementMutation.SET_RENDER_UPDATE);
        this.context.dispatch(ContentManagementActions.REFRESH_PROMOTIONS);

        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.UPDATE_PROMOTION](payload) {

    return new Promise((resolve, reject) => {

      const { editId, data } = payload;

      apiService.put(`content/update-promotion/${editId}`, data).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        this.context.dispatch(ContentManagementActions.REFRESH_PROMOTIONS);

        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.UPDATE_PROMOTION_TRANSLATION](payload) {

    return new Promise<void>((resolve, reject) => {

      const { editId, data } = payload;

      apiService.put(`content/update-promotion-translation/${editId}`, data).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.DELETE_PROMOTION](editId) {

    return new Promise((resolve, reject) => {

      apiService.delete(`content/delete-promotion/${editId}`).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        this.context.dispatch(ContentManagementActions.REFRESH_PROMOTIONS);

        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [ContentManagementActions.GET_PROMOTION_OPTIONS]() {

    return new Promise<void>((resolve, reject) => {

      apiService.get("content/get-promotion-options").then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        this.context.commit(ContentManagementMutation.SET_PROMOTION_OPTIONS, {
          promotionOptions: data.result
        });

        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

}
